Look at how u
<template>
    <v-card flat color="transparent" class="w-100 align-self-start">
        <v-card-title>
            <span class="secondary--text">Pools</span>
        </v-card-title>
        <v-card-text>
            <div class="d-flex align-center">
                <v-menu offset-y>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn v-bind="attrs" v-on="on" color="primary" class="mr-2">
                            <v-icon left>mdi-plus</v-icon>
                            Pool
                            <v-icon right>mdi-chevron-down</v-icon>
                        </v-btn>
                    </template>
                    <v-list dense nav>
                        <v-list-item :to="{ name: 'user-pools-create-account' }">
                            <v-list-item-icon>
                                <v-icon>mdi-account-box-outline</v-icon>
                            </v-list-item-icon>
                            <v-list-item-title class="mr-4">Account</v-list-item-title>
                        </v-list-item>
                        <v-list-item :to="{ name: 'user-pools-create-wallet' }">
                            <v-list-item-icon>
                                <v-icon>mdi-wallet-outline</v-icon>
                            </v-list-item-icon>
                            <v-list-item-title class="mr-4">Wallet</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>

                <v-spacer></v-spacer>

                <v-text-field
                    prepend-inner-icon="mdi-magnify "
                    autofocus
                    hide-details
                    solo
                    flat
                    dense
                    placeholder="Search for a pool..."
                    v-model="searchPool"
                    clearable
                    class="mr-2">
                    <template v-slot:append>
                        <PoolTypeFilterMenu v-model="selectedPoolTypes" />
                    </template>
                </v-text-field>

                <v-btn icon :loading="loading" @click="refreshPools">
                    <v-icon>refresh</v-icon>
                </v-btn>
            </div>

            <v-alert v-if="error" type="error" text dismissible class="mt-4 mb-0" @input="error = ''">{{ error }}</v-alert>
            <v-data-table
                :headers="headers"
                :items="filteredPools"
                :items-per-page.sync="itemsPerPage"
                :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
                :search="searchPool"
                :loading="loading"
                class="mt-4 pools-table">
                <template v-slot:no-data>
                    <span>No pools found.</span>
                </template>

                <template v-slot:[`item.name`]="{ item }">
                    <div class="d-flex align-center">
                        <v-icon left small>{{ item.poolType === 'accounts' ? 'mdi-account-box-outline' : 'mdi-wallet-outline' }}</v-icon>
                        <span>{{ item.name }}</span>
                    </div>
                </template>
                <template v-slot:[`item.amount`]="{ item }">
                    <span v-if="item.amount !== null">{{ item.amount }}</span>
                    <span v-else>-</span>
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                    <div class="text-right">
                        <v-menu offset-y left>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn text color="secondary" small v-bind="attrs" v-on="on">
                                    <v-icon>mdi-dots-horizontal</v-icon>
                                </v-btn>
                            </template>
                            <v-list dense nav>
                                <v-list-item @click="goToPoolSettings(item)">
                                    <v-list-item-icon>
                                        <v-icon>mdi-pencil-outline</v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-title class="mr-4">Edit</v-list-item-title>
                                </v-list-item>
                                <v-list-item @click="goToPoolResources(item)">
                                    <v-list-item-icon>
                                        <v-icon>mdi-sitemap-outline</v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-title class="mr-4">Resources</v-list-item-title>
                                </v-list-item>
                                <v-divider class="my-2"></v-divider>
                                <v-list-item
                                    :disabled="item.poolType !== 'accounts' || creatingDeletionRequest === item.rpid"
                                    @click="createDeletionRequest(item)">
                                    <v-list-item-icon>
                                        <v-progress-circular
                                            v-if="creatingDeletionRequest === item.rpid"
                                            color="error"
                                            size="18"
                                            width="2"
                                            indeterminate></v-progress-circular>
                                        <v-icon v-else color="error">mdi-trash-can-outline</v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-title class="error--text mr-4">Request deletion</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </div>
                </template>
            </v-data-table>
        </v-card-text>
    </v-card>
</template>

<script>
import { mapState } from 'vuex'

const PoolTypeFilterMenu = () => import('../components/PoolTypeFilterMenu.vue')

export default {
    name: 'UserPools',
    components: {
        PoolTypeFilterMenu
    },
    data() {
        return {
            searchPool: '',
            selectedPoolTypes: ['accounts', 'wallets'],
            itemsPerPage: 100,
            itemsPerPageOptions: [-1, 10, 50, 100],
            loading: false,
            error: '',
            creatingDeletionRequest: null,
            headers: [
                { text: 'Name', value: 'name', align: 'left' },
                { text: 'Amount', value: 'amount', align: 'left' },
                { text: 'Parent account', value: 'parentAccount', align: 'left' },
                { text: 'Ancestor', value: 'ancestor', align: 'left' },
                { text: 'Actions', value: 'actions', align: 'right', sortable: false }
            ]
        }
    },
    computed: {
        ...mapState(['userOrgs', 'currentOrgData']),
        ...mapState('userStore', ['selectedResourcePool', 'availableResourcePools']),
        currentOrgOid() {
            return this.$route.params.oid || this.currentOrgData?.oid || this.userOrgs[0]?.oid
        },
        allPools() {
            return this.availableResourcePools.map(pool => {
                const parentPool = this.availableResourcePools.find(candidate => Number(candidate.rpid) === Number(pool.parent_rpid))
                const rootPool = this.availableResourcePools.find(candidate => Number(candidate.rpid) === Number(pool.root_rpid))
                const poolType = String(pool.resource_pool_type || 'ACCOUNT').toLowerCase() === 'wallet' ? 'wallets' : 'accounts'

                return {
                    rpid: pool.rpid,
                    poolType,
                    name: pool.long_id,
                    amount: pool.allocated_amount ?? pool.balance ?? null,
                    parentAccount: pool.parent_long_id || parentPool?.long_id || '-',
                    ancestor: pool.root_long_id || rootPool?.long_id || (poolType === 'accounts' ? pool.long_id : '-'),
                    isManager: Boolean(pool.is_rp_manager)
                }
            })
        },
        filteredPools() {
            return this.allPools.filter(pool => this.selectedPoolTypes.includes(pool.poolType))
        }
    },
    methods: {
        async refreshPools() {
            this.loading = true
            this.error = ''
            try {
                const { data: availablePools } = await this.$axios.get('/resource/available_rps')
                this.$store.commit('userStore/setAvailableResourcePoolData', availablePools)
            } catch (error) {
                this.error = error.response?.data?.debug || 'Could not load resource pools.'
            } finally {
                this.loading = false
            }
        },
        goToPoolSettings(pool) {
            if (!this.currentOrgOid || !pool.rpid) {
                return
            }

            this.$router.push({
                name: 'config-pool-general',
                params: {
                    oid: this.currentOrgOid,
                    rpid: pool.rpid
                }
            })
        },
        goToPoolResources(pool) {
            this.$router.push({ name: 'reporting-rp-overview', params: { rpid: pool.rpid } })
        },
        async createDeletionRequest(pool) {
            if (pool.poolType !== 'accounts' || !pool.isManager) return
            this.creatingDeletionRequest = pool.rpid
            try {
                await this.$axios.post(`/resource_pools/${pool.rpid}/deletion_requests`)
                this.$store.dispatch('showSnackBar', {
                    snackBarText: 'Deletion request created.',
                    snackBarTimeout: 5000,
                    snackBarIcon: 'check_circle'
                })
            } catch (error) {
                this.error = error.response?.data?.debug || 'Could not create a deletion request.'
            } finally {
                this.creatingDeletionRequest = null
            }
        }
    },
    created() {
        this.refreshPools()
    }
}
</script>

<style scoped lang="scss">
.pools-table {
    ::v-deep .v-data-table__wrapper > table {
        table-layout: fixed;
        width: 100%;
    }
}
</style>
